export function copy(text: string) {
  const { isApple, isMobile } = useDevice();
  return new Promise((resolve, reject) => {
    // iOS Safari 13.4+ 支持 navigator.clipboard.writeText，优先使用
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => resolve(true))
        .catch(() => {
          // 如果 writeText 失败，降级到 textarea 方案
          fallbackCopy(text, isApple, isMobile).then(resolve).catch(reject);
        });
    } else if (navigator?.clipboard && navigator?.permissions) {
      // 尝试使用 ClipboardItem API（某些浏览器支持）
      const type = 'text/plain';
      const blob = new Blob([text], { type });
      const data = [new ClipboardItem({ [type]: blob })];
      navigator.permissions
        .query({ name: 'clipboard-write' as PermissionName })
        .then(permission => {
          if (['prompt', 'granted'].includes(permission.state)) {
            navigator.clipboard
              .write(data)
              .then(() => resolve(true))
              .catch(() => {
                // 如果失败，降级到 textarea 方案
                fallbackCopy(text, isApple, isMobile).then(resolve).catch(reject);
              });
          } else {
            // 权限被拒绝，降级到 textarea 方案
            fallbackCopy(text, isApple, isMobile).then(resolve).catch(reject);
          }
        })
        .catch(() => {
          // 权限查询失败，降级到 textarea 方案
          fallbackCopy(text, isApple, isMobile).then(resolve).catch(reject);
        });
    } else if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
      // 降级方案：使用 textarea + execCommand
      fallbackCopy(text, isApple, isMobile).then(resolve).catch(reject);
    } else {
      reject(new Error('None of copying methods are supported by this browser!'));
    }
  });
}

// iOS 移动端降级复制方案
function fallbackCopy(text: string, isApple: boolean, isMobile: boolean): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;

    // 保存当前滚动位置
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    // iOS Safari 需要 textarea 可见且可编辑（不能是 readOnly）
    if (isApple && isMobile) {
      textarea.style.position = 'fixed';
      textarea.style.top = '-100px';
      textarea.style.left = '-100px';
      textarea.style.width = '2em';
      textarea.style.height = '2em';
      textarea.style.padding = '0';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      textarea.style.visibility = 'hidden';
      textarea.style.zIndex = '-9999';
      // iOS Safari 不能设置 readOnly，必须是可编辑的
    } else {
      // 其他设备可以使用 readOnly
      textarea.style.position = 'fixed';
      textarea.style.top = '-100px';
      textarea.style.left = '-100px';
      textarea.style.width = '2em';
      textarea.style.height = '2em';
      textarea.style.padding = '0px';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      textarea.style.visibility = 'hidden';
      textarea.readOnly = true;
    }

    document.body.appendChild(textarea);

    // iOS Safari 需要先 focus，然后使用 setSelectionRange
    if (isApple && isMobile) {
      textarea.focus({ preventScroll: true });
      textarea.setSelectionRange(0, text.length);
    } else {
      textarea.focus({ preventScroll: true });
      textarea.select();
    }

    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      // 恢复滚动位置
      window.scrollTo(scrollLeft, scrollTop);

      if (successful) {
        resolve(true);
      } else {
        reject(new Error('Copy command failed'));
      }
    } catch (error) {
      document.body.removeChild(textarea);

      // 恢复滚动位置
      window.scrollTo(scrollLeft, scrollTop);

      reject(error);
    }
  });
}
